import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PlanoEscolha from '@/components/feature/PlanoEscolha';
import DesbloqueioConfianca from '@/components/feature/DesbloqueioConfianca';
import { supabase, edgeFunctionErrorBody } from '@/lib/supabase';
import {
  planosDisponiveis,
  PlanConfig,
  PlanInfo,
  loadSubscriptionFallback,
  fetchPlanosFromDB,
  estadoExibidoDaAssinatura,
} from '@/lib/planConfig';
import { fmtMoeda, fmtData } from '@/pages/plan/shared';

interface SubscriptionData {
  planId: string | null;
  status: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  condominiosMax: number;
  usuariosMax: number;
  condominiosAtual: number;
  usuariosAtual: number;
  stripeStatus?: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  amount?: number;
  currency?: string;
  productName?: string;
  detectedPlanId?: string;
  planSynced?: boolean;
  paymentMethod?: {
    type: string;
    brand: string | null;
    last4: string | null;
  };
  stripeError?: boolean;
  stripeConfigured?: boolean;
  stripeErrorMessage?: string;
  planName?: string;
  planPrice?: number;
}

const paymentMethodLabels: Record<string, { icon: string; label: string }> = {
  card: { icon: 'ri-bank-card-line', label: 'Cartao' },
  boleto: { icon: 'ri-barcode-line', label: 'Boleto' },
  pix: { icon: 'ri-qr-code-line', label: 'Pix' },
};

const statusConfig: Record<string, { label: string; cor: string; desc: string }> = {
  active: {
    label: 'Ativo',
    cor: 'bg-green-50 text-green-700 border-green-200',
    desc: 'Sua assinatura esta ativa.',
  },
  past_due: {
    label: 'Pendente',
    cor: 'bg-amber-50 text-amber-700 border-amber-200',
    desc: 'Ha uma cobranca pendente. Atualize seu metodo de pagamento.',
  },
  unpaid: {
    label: 'Pagamento recusado',
    cor: 'bg-red-50 text-red-700 border-red-200',
    desc: 'O pagamento foi recusado. Atualize seu metodo de pagamento.',
  },
  cancel_at_period_end: {
    label: 'Cancelamento agendado',
    cor: 'bg-amber-50 text-amber-700 border-amber-200',
    desc: 'Sua assinatura sera cancelada ao final do periodo atual.',
  },
  canceled: {
    label: 'Cancelado',
    cor: 'bg-red-50 text-red-700 border-red-200',
    desc: 'Sua assinatura foi cancelada.',
  },
  // Com boleto, e o estado entre gerar o boleto e o banco compensar (ate 1 dia
  // util depois do pagamento). Dizer "nao concluido" a quem acabou de pagar
  // sugeria que a compra tinha falhado.
  incomplete: {
    label: 'Aguardando pagamento',
    cor: 'bg-amber-50 text-amber-700 border-amber-200',
    desc: 'Estamos aguardando a confirmacao do pagamento. Boletos sao confirmados pelo banco em ate 1 dia util apos o pagamento.',
  },
  inactive: {
    label: 'Sem plano',
    cor: 'bg-background-100 text-foreground-600 border-background-200',
    desc: 'Voce ainda nao possui um plano ativo.',
  },
};

export default function PlanoPage() {
  const [subData, setSubData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [funcError, setFuncError] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelResult, setCancelResult] = useState<{
    message: string;
    currentPeriodEnd: string;
  } | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSubscription = async () => {
    setLoading(true);
    setFuncError(false);

    let dataFromEdge: any = null;

    // 1. Try edge function first
    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-subscription', {});

      if (!fnError && data && !data.internalError) {
        dataFromEdge = data;
      } else {
        // Edge function had issues — mark for fallback
        setFuncError(true);
        const body = fnError ? await edgeFunctionErrorBody(fnError) : null;
        console.warn('Edge function issue:', body?.error || fnError?.message || data?.errorMessage);
      }
    } catch (err: any) {
      setFuncError(true);
      console.warn('Edge function unreachable:', err?.message);
    }

    // 2. Merge with DB fallback (always run for reliability)
    let fallbackData = null;
    try {
      fallbackData = await loadSubscriptionFallback();
    } catch {
      /* ignore */
    }

    // 3. Build final data: prefer edge function, fill gaps with fallback
    if (dataFromEdge) {
      // If edge function returned but fallback has newer plan data (e.g., from sync), use fallback's plan
      if (fallbackData) {
        setSubData({
          ...dataFromEdge,
          // Use fallback data if edge function didn't return these
          planId: dataFromEdge.planId || fallbackData.planId,
          status: dataFromEdge.status || fallbackData.status,
          condominiosMax: dataFromEdge.condominiosMax || fallbackData.condominiosMax,
          usuariosMax: dataFromEdge.usuariosMax || fallbackData.usuariosMax,
          condominiosAtual: dataFromEdge.condominiosAtual ?? fallbackData.condominiosAtual,
          usuariosAtual: dataFromEdge.usuariosAtual ?? fallbackData.usuariosAtual,
        });
      } else {
        setSubData(dataFromEdge);
      }
    } else if (fallbackData) {
      // Only fallback available — use it
      setSubData({
        ...fallbackData,
        stripeConfigured: false,
      });
    } else {
      // Nothing at all — show empty state, not error
      setSubData({
        planId: null,
        status: 'inactive',
        stripeSubscriptionId: null,
        stripeCustomerId: null,
        condominiosMax: 0,
        usuariosMax: 0,
        condominiosAtual: 0,
        usuariosAtual: 0,
        stripeConfigured: false,
      });
    }

    setLoading(false);
  };

  // Em estado: a lista do modulo e atualizada por fora do React, entao ler
  // `planosDisponiveis` no render mostrava o FALLBACK ate o proximo render por
  // outro motivo (ver planConfig).
  const [planos, setPlanos] = useState<PlanInfo[]>(planosDisponiveis);

  useEffect(() => {
    let ativo = true;

    fetchPlanosFromDB().then((lista) => {
      if (ativo) setPlanos(lista);
    });
    fetchSubscription();

    return () => {
      ativo = false;
    };
  }, []);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const basePath = (window as any).__BASE_PATH__ || '';
      const origin = window.location.origin;
      const returnUrl = `${origin}${basePath}/gestao-plano/plano`;

      const { data, error: fnError } = await supabase.functions.invoke('create-portal-session', {
        body: { returnUrl },
      });

      if (fnError || data?.error) {
        const body = fnError ? await edgeFunctionErrorBody(fnError) : null;
        const errMsg = data?.error || body?.error || fnError?.message || 'Erro ao abrir portal.';
        if (
          errMsg.toLowerCase().includes('stripe') &&
          errMsg.toLowerCase().includes('configurado')
        ) {
          showToast('Stripe ainda nao esta configurado. Entre em contato com o suporte.', 'error');
        } else if (errMsg.toLowerCase().includes('cliente stripe nao encontrado')) {
          showToast(
            'Voce ainda nao tem um cliente no Stripe. Contrate um plano primeiro.',
            'error',
          );
        } else {
          showToast(errMsg, 'error');
        }
        setPortalLoading(false);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      showToast(err?.message || 'Erro ao conectar com o Stripe.', 'error');
      setPortalLoading(false);
    }
  };

  // Quem cria conta em `/register` e sai antes de escolher plano volta a
  // acessar a aplicacao com uma conta valida e sem assinatura: o `PlanGuard`
  // manda essa pessoa para ca, e o passo de escolha de plano precisa
  // acontecer aqui mesmo — mandar de volta para `/register` a fazia esbarrar
  // no "e-mail ja cadastrado", sem saida (P-41). `create-checkout-session` ja
  // identifica a conta pelo JWT da sessao e reaproveita o perfil existente,
  // entao nao ha chamada de backend diferente da usada no cadastro.
  const handleCheckoutPlano = async (planoId: string): Promise<string | void> => {
    const basePath = (window as any).__BASE_PATH__ || '';
    const origin = window.location.origin;
    const successUrl = `${origin}${basePath}/dashboard`;
    const cancelUrl = `${origin}${basePath}/gestao-plano/plano`;

    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-checkout-session', {
        body: { planId: planoId, successUrl, cancelUrl },
      });

      if (fnError || data?.error) {
        const body = fnError ? await edgeFunctionErrorBody(fnError) : null;
        if ((data?.codigo || body?.codigo) === 'assinatura_vigente') {
          return 'Esta conta já possui uma assinatura ativa.';
        }
        return (
          data?.error ||
          body?.error ||
          fnError?.message ||
          'Erro ao iniciar pagamento. Tente novamente.'
        );
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      return 'Não foi possível iniciar o checkout. Tente novamente.';
    } catch (err: any) {
      return err?.message || 'Erro ao conectar com o Stripe. Tente novamente.';
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('cancel-subscription', {});
      if (fnError || data?.error) {
        const body = fnError ? await edgeFunctionErrorBody(fnError) : null;
        const errMsg =
          data?.error || body?.error || fnError?.message || 'Erro ao cancelar assinatura.';
        if (
          errMsg.toLowerCase().includes('stripe') &&
          errMsg.toLowerCase().includes('configurado')
        ) {
          showToast('Stripe ainda nao esta configurado. Entre em contato com o suporte.', 'error');
        } else if (errMsg.toLowerCase().includes('nenhuma assinatura')) {
          showToast('Voce nao possui uma assinatura ativa no Stripe.', 'error');
        } else {
          showToast(errMsg, 'error');
        }
        setCanceling(false);
        setShowCancelModal(false);
        return;
      }

      setCancelResult(data);
      setShowCancelModal(false);
      showToast('Assinatura sera cancelada ao final do periodo.', 'success');

      await fetchSubscription();
    } catch (err: any) {
      showToast(err?.message || 'Erro ao cancelar assinatura.', 'error');
      setShowCancelModal(false);
    } finally {
      setCanceling(false);
    }
  };

  const displayStatus = estadoExibidoDaAssinatura(subData?.status, subData?.cancelAtPeriodEnd);
  const statusInfo = statusConfig[displayStatus] || statusConfig.inactive;

  const resolvedPlanId = subData?.planId || subData?.detectedPlanId;
  const planConfig = resolvedPlanId ? PlanConfig[resolvedPlanId] : undefined;
  const planoAtual = planConfig;

  const usoPercent =
    planoAtual && planoAtual.condominiosMax > 0
      ? Math.round(((subData?.condominiosAtual || 0) / planoAtual.condominiosMax) * 100)
      : 0;
  const usuariosPercent =
    planoAtual && planoAtual.usuariosMax > 0
      ? Math.round(((subData?.usuariosAtual || 0) / planoAtual.usuariosMax) * 100)
      : 0;

  const preco = subData?.amount ? subData.amount / 100 : planoAtual?.preco || 0;
  const pmInfo = subData?.paymentMethod;
  const pmLabel = pmInfo
    ? paymentMethodLabels[pmInfo.type] || { icon: 'ri-bank-card-line', label: pmInfo.type }
    : null;

  const hasStripeCustomer = !!subData?.stripeCustomerId;
  const isStripeConfigured = subData?.stripeConfigured !== false;

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Seu Plano</h1>
          <p className="text-sm text-foreground-500 mt-1">Visualize e gerencie sua assinatura.</p>
        </div>

        <div className="flex flex-col gap-6">
          {/* Loading */}
          {loading && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 flex items-center justify-center">
              <div className="text-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
                <p className="text-sm text-foreground-500 mt-3">
                  Carregando dados da assinatura...
                </p>
              </div>
            </div>
          )}

          {/* Edge function warning (non-blocking) */}
          {!loading && funcError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-600 text-[10px]"></i>
              </div>
              <p className="text-xs text-amber-700">
                Dados carregados do banco local. Algumas informacoes do Stripe podem estar
                indisponiveis no momento.
                <button
                  onClick={fetchSubscription}
                  className="ml-2 underline hover:text-amber-900 font-medium whitespace-nowrap"
                >
                  Tentar novamente
                </button>
              </p>
            </div>
          )}

          {/* Plan card — always shown when not loading */}
          {!loading && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              {/* Header */}
              <div className="px-5 py-4 border-b border-background-200/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <i className="ri-vip-crown-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-foreground-900">
                        {planoAtual ? `Plano ${planoAtual.nome}` : 'Sem plano ativo'}
                      </h3>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusInfo.cor}`}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-500">
                      {planoAtual ? (
                        <>
                          {fmtMoeda(preco)}/mes
                          {subData?.currentPeriodEnd && (
                            <> — Proxima cobranca em {fmtData(subData.currentPeriodEnd)}</>
                          )}
                        </>
                      ) : (
                        statusInfo.desc
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {hasStripeCustomer && isStripeConfigured && displayStatus !== 'canceled' && (
                    <>
                      <button
                        onClick={handleOpenPortal}
                        disabled={portalLoading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                      >
                        {portalLoading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Abrindo...
                          </>
                        ) : (
                          <>
                            <i className="ri-settings-3-line"></i>
                            Gerenciar Assinatura
                          </>
                        )}
                      </button>
                      {displayStatus !== 'cancel_at_period_end' && (
                        <button
                          onClick={() => setShowCancelModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-circle-line"></i>
                          Cancelar
                        </button>
                      )}
                    </>
                  )}
                  {planoAtual && !hasStripeCustomer && displayStatus !== 'canceled' && (
                    <span className="text-xs text-foreground-500">
                      <i className="ri-information-line mr-1"></i>
                      Pagamento ainda nao configurado
                    </span>
                  )}
                </div>
              </div>

              {/* Cancel warning banner */}
              {displayStatus === 'cancel_at_period_end' && subData?.currentPeriodEnd && (
                <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-timer-line text-amber-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Cancelamento agendado para {fmtData(subData.currentPeriodEnd)}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Seu acesso permanece ativo ate essa data. Depois disso, a assinatura sera
                      encerrada.
                    </p>
                  </div>
                </div>
              )}

              {/* Past due warning */}
              {(displayStatus === 'past_due' || displayStatus === 'unpaid') && (
                <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-red-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-800">Problema no pagamento</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      {statusInfo.desc} Clique em "Gerenciar Assinatura" para atualizar seus dados.
                    </p>
                  </div>
                </div>
              )}

              {/* Pagamento pendente: verificar de novo sem recarregar a pagina */}
              {displayStatus === 'incomplete' && (
                <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-time-line text-amber-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Pagamento em confirmacao</p>
                      <p className="text-xs text-amber-700 mt-0.5">{statusInfo.desc}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={fetchSubscription}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-amber-300 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    Verificar pagamento
                  </button>
                </div>
              )}

              {/* Desbloqueio em confianca: boleto pago, compensacao pendente (P-46).
                  A funcao decide se ha fatura de boleto elegivel; fora disso nao aparece. */}
              {['incomplete', 'past_due', 'unpaid'].includes(displayStatus) && (
                <DesbloqueioConfianca />
              )}

              {/* Plan sync notice */}
              {!loading && subData?.planSynced === true && (
                <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-refresh-line text-green-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Plano sincronizado com o Stripe
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      Seu plano foi atualizado com base nos dados do Stripe. As informacoes agora
                      estao consistentes.
                    </p>
                  </div>
                </div>
              )}

              {/* Details */}
              {planoAtual && (
                <>
                  <div className="p-5 md:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Usage */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">
                        Consumo do Plano
                      </h4>
                      <div className="flex flex-col gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm text-foreground-600">Condominios</span>
                            <span className="text-sm font-semibold text-foreground-800">
                              {subData?.condominiosAtual || 0} /{' '}
                              {planoAtual.condominiosMax === 99
                                ? 'Ilimitado'
                                : planoAtual.condominiosMax}
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-background-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary-500 transition-all"
                              style={{
                                width: `${planoAtual.condominiosMax >= 99 ? Math.min(usoPercent, 95) : Math.min(usoPercent, 100)}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm text-foreground-600">Usuarios da equipe</span>
                            <span className="text-sm font-semibold text-foreground-800">
                              {subData?.usuariosAtual || 0} / {planoAtual.usuariosMax}
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-background-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-accent-500 transition-all"
                              style={{ width: `${Math.min(usuariosPercent, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {pmLabel && (
                        <div className="mt-5 pt-4 border-t border-background-200/70">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                            Metodo de pagamento
                          </h4>
                          <div className="flex items-center gap-2 text-sm text-foreground-700">
                            <i className={`${pmLabel.icon} text-lg`}></i>
                            <span>{pmLabel.label}</span>
                            {pmInfo?.brand && pmInfo?.last4 && (
                              <span className="text-foreground-500">
                                • {pmInfo.brand.charAt(0).toUpperCase() + pmInfo.brand.slice(1)}{' '}
                                terminado em {pmInfo.last4}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">
                        Recursos incluidos
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {planoAtual.recursos.map((r) => (
                          <div
                            key={r}
                            className="flex items-center gap-2 text-sm text-foreground-700"
                          >
                            <div className="w-4 h-4 flex items-center justify-center text-green-600 flex-shrink-0">
                              <i className="ri-checkbox-circle-fill text-xs"></i>
                            </div>
                            {r}
                          </div>
                        ))}
                      </div>

                      {subData?.currentPeriodStart && subData?.currentPeriodEnd && (
                        <div className="mt-5 pt-4 border-t border-background-200/70">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                            Periodo atual
                          </h4>
                          <p className="text-sm text-foreground-600">
                            {fmtData(subData.currentPeriodStart)} —{' '}
                            {fmtData(subData.currentPeriodEnd)}
                          </p>
                          {subData?.stripeSubscriptionId && (
                            <p className="text-xs text-foreground-400 mt-1">
                              ID: {subData.stripeSubscriptionId.substring(0, 18)}...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* No plan CTA */}
              {!planoAtual && displayStatus === 'inactive' && (
                <div className="p-6 md:p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-store-2-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-900 mb-2">
                      Nenhum plano ativo
                    </p>
                    <p className="text-sm text-foreground-500">
                      Escolha um plano para comecar a usar todos os recursos do Central Check.
                    </p>
                  </div>
                  <PlanoEscolha planos={planos} onCheckout={handleCheckoutPlano} />
                </div>
              )}

              {/* Canceled state */}
              {displayStatus === 'canceled' && (
                <div className="p-6 md:p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-history-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-900 mb-2">
                      Assinatura cancelada
                    </p>
                    <p className="text-sm text-foreground-500">
                      Sua assinatura foi encerrada. Para voltar a usar o Central Check, escolha um
                      novo plano.
                    </p>
                  </div>
                  <PlanoEscolha planos={planos} onCheckout={handleCheckoutPlano} />
                </div>
              )}
            </div>
          )}

          {/* Alterar Plano — only when there is data */}
          {!loading && planoAtual && displayStatus === 'active' && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900">Alterar Plano</h4>
                  <p className="text-xs text-foreground-500 mt-1">
                    Precisa de mais recursos? Faca upgrade para um plano superior. Quer reduzir
                    custos? Faca downgrade — tudo pelo Portal do Stripe, com seguranca e sem
                    interrupcao.
                  </p>
                </div>
                {hasStripeCustomer && isStripeConfigured ? (
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap flex-shrink-0"
                  >
                    {portalLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Abrindo portal...
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-left-right-line"></i>
                        Abrir Portal do Stripe
                      </>
                    )}
                  </button>
                ) : (
                  <span className="text-xs text-foreground-500 flex-shrink-0">
                    <i className="ri-information-line mr-1"></i>
                    Portal disponivel apos configuracao do Stripe
                  </span>
                )}
              </div>

              {/* Available plans preview */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {planos.map((plano) => {
                  const isCurrent = plano.id === resolvedPlanId;
                  return (
                    <div
                      key={plano.id}
                      className={`rounded-lg border p-4 transition-colors ${
                        isCurrent
                          ? 'border-primary-300 bg-primary-50/40'
                          : 'border-background-200/70 bg-background-50 hover:border-background-300/60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {plano.nome}
                        </span>
                        {isCurrent && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-500 text-background-50">
                            ATUAL
                          </span>
                        )}
                      </div>
                      <p className="text-lg font-bold text-foreground-900 mb-1">
                        {fmtMoeda(plano.preco)}
                        <span className="text-xs font-normal text-foreground-500">/mes</span>
                      </p>
                      <p className="text-xs text-foreground-500 mb-3">{plano.descricao}</p>
                      <div className="flex flex-col gap-1">
                        {plano.recursos.slice(0, 3).map((r) => (
                          <div
                            key={r}
                            className="flex items-center gap-1.5 text-xs text-foreground-600"
                          >
                            <div className="w-3 h-3 flex items-center justify-center text-green-600 flex-shrink-0">
                              <i className="ri-check-line text-[10px]"></i>
                            </div>
                            {r}
                          </div>
                        ))}
                        {plano.recursos.length > 3 && (
                          <span className="text-xs text-foreground-400 mt-0.5">
                            +{plano.recursos.length - 3} recursos
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* History */}
          {!loading && planoAtual && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h4 className="text-sm font-semibold text-foreground-900 mb-4">Historico do Plano</h4>
              <div className="relative pl-6">
                <div className="absolute left-[9px] top-2 bottom-2 w-px bg-background-200/70"></div>
                <div className="flex flex-col gap-4">
                  {[
                    {
                      evento: planoAtual ? `Plano ${planoAtual.nome} ativo` : 'Assinatura',
                      data: subData?.currentPeriodStart || new Date().toISOString().split('T')[0],
                      desc: planoAtual
                        ? `${fmtMoeda(preco)}/mes — ${planoAtual.condominiosMax === 99 ? 'Ilimitado' : `Ate ${planoAtual.condominiosMax}`} condominios`
                        : 'Assinatura ativa',
                    },
                  ].map((h, idx) => (
                    <div key={idx} className="flex items-start gap-3 relative">
                      <div
                        className={`relative z-10 mt-1.5 w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${
                          idx === 0
                            ? 'bg-primary-500 border-primary-500'
                            : 'bg-background-50 border-background-300'
                        }`}
                      ></div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{h.evento}</p>
                        <p className="text-xs text-foreground-500">
                          {fmtData(h.data)} — {h.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cancel confirmation modal */}
        {showCancelModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowCancelModal(false)}
            ></div>
            <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900 text-center mb-2">
                Cancelar assinatura?
              </p>
              <p className="text-sm text-foreground-600 text-center mb-2">
                Seu plano <strong>{planoAtual?.nome}</strong> sera cancelado ao final do periodo
                atual
                {subData?.currentPeriodEnd && <> ({fmtData(subData.currentPeriodEnd)})</>}.
              </p>
              <p className="text-xs text-foreground-500 text-center mb-6">
                Voce continuara tendo acesso a todos os recursos ate essa data. Depois, seus dados
                permanecem salvos mas o acesso sera limitado.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={canceling}
                  className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Voltar
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="px-5 py-2.5 rounded-md bg-red-600 text-background-50 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {canceling ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <i className="ri-close-circle-line"></i>
                      Sim, cancelar assinatura
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel result modal */}
        {cancelResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setCancelResult(null)}
            ></div>
            <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-timer-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900 text-center mb-2">
                Cancelamento agendado
              </p>
              <p className="text-sm text-foreground-600 text-center mb-6">
                Sua assinatura sera cancelada em{' '}
                <strong>{fmtData(cancelResult.currentPeriodEnd)}</strong>. Ate la, voce continua com
                acesso normal a todos os recursos.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => setCancelResult(null)}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50">
            <div
              className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
                toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-green-50 text-green-800 border border-green-200'
              }`}
            >
              <i
                className={
                  toast.type === 'error'
                    ? 'ri-error-warning-line text-red-600'
                    : 'ri-checkbox-circle-line text-green-600'
                }
              ></i>
              {toast.msg}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
